import React, { useState } from 'react'
import { fontSize, spacing, Text, Tooltip } from '@retailer-platform/shared-components'
import { ConfirmIcon, HelpIcon, PlusIcon, TrashIcon } from '@instacart/ids-core'
import { SwitchBase } from '@instacart/ids-tooling'
import { Divider, IconButton } from '@instacart/ids-customers'
import styled from '@emotion/styled'
import {
  Container,
  Input,
  Property,
  PropertyContainer,
  PropertyRow,
  PropertySectionLeft,
  PropertySectionRight,
  PropertyTitle,
  SectionHeader,
  TooltipContainerNotFullWidth,
} from '../../shared-styled-components/SharedStyledComponents'
import { useDomainMessages } from '../../../utils/domain/intl'
import { TextInput } from '../../../utils/common.styles'
import { type FormProps } from './StepAttributeForm'

const DeduplicationGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: ${spacing.X16};
  width: 100%;
`

const ExampleGrid = styled.div`
  display: grid;
  grid-template-columns: auto auto auto auto;
  gap: ${spacing.X16};
`

const ExampleGridItem = styled(Text)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const SnowflakeQueryInputForm = ({ attributes, mode, onChange }: FormProps) => {
  const i18n = useDomainMessages({
    limit:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.limit',
    limitTooltip:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.limit.help',
    queryTooltip:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.query.help',
    title:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.title',
    outputExample:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.outputExample',
    queryResponseExample:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.queryResponseExample',
    queryResponseExampleEntityId:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.queryResponseExample.entity_id',
    queryResponseExampleEntityType:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.queryResponseExample.entity_type',
    queryResponseExampleDisplayName:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.queryResponseExample.display_name',
    queryResponseExampleImageUrl:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.queryResponseExample.image_url',
    entityIdExample1:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.queryResponseExample.entity_id.example1',
    entityTypeExample:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.queryResponseExample.entity_type.example',
    displayNameExample1:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.queryResponseExample.display_name.example1',
    imageUrlExample1:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.queryResponseExample.image_url.example1',
    entityIdExample2:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.queryResponseExample.entity_id.example2',
    displayNameExample2:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.queryResponseExample.display_name.example2',
    entityIdExample3:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.queryResponseExample.entity_id.example3',
    displayNameExample3:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.queryResponseExample.display_name.example3',
    imageUrlExample3:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.queryResponseExample.image_url.example3',
    entityIdExample4:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.queryResponseExample.entity_id.example4',
    displayNameExample4:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.queryResponseExample.display_name.example4',
    imageUrlExample4:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.queryResponseExample.image_url.example4',
    entityIdExample5:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.queryResponseExample.entity_id.example5',
    displayNameExample5:
      'catalogAdminDomain.attributeManagement.stepAttributeForms.snowflakeQueryInputForm.queryResponseExample.display_name.example5',
  })
  const [newDeduplicationField, setNewDeduplicationField] = useState('')
  return (
    <>
      <SectionHeader> {i18n.title} </SectionHeader>
      <PropertyContainer>
        <PropertySectionLeft>
          <Container style={{ marginTop: spacing.X16 }}>
            <Text weight="bold">Query Settings</Text>
            <PropertyRow>
              <PropertyTitle>
                {i18n.limit}
                <TooltipContainerNotFullWidth>
                  <Tooltip placement="bottom" target={<HelpIcon />}>
                    {i18n.limitTooltip}
                  </Tooltip>
                </TooltipContainerNotFullWidth>
              </PropertyTitle>
              <Input
                type="number"
                value={attributes.limit}
                disabled={mode === 'view'}
                onChange={event =>
                  onChange({
                    ...attributes,
                    limit: Math.min(Math.max(Number(event.target.value), 1), 100000),
                  })
                }
              />
            </PropertyRow>
            <PropertyRow>
              <PropertyTitle>
                Ranking field
                <TooltipContainerNotFullWidth>
                  <Tooltip placement="bottom" target={<HelpIcon />}>
                    Type the name of the column you&apos;d like to rank your results by. Top results
                    are first to be turned into tasks. Biggest numbers first. If this field is
                    provided, records without this field will be skipped and no task will be created
                    for them.
                  </Tooltip>
                </TooltipContainerNotFullWidth>
              </PropertyTitle>
              <SwitchBase
                checked={attributes.useRanking}
                disabled={mode !== 'edit'}
                description="Enable ranking"
                onChange={event => onChange({ ...attributes, useRanking: event.target.checked })}
              />
              <Input
                type="text"
                value={attributes.useRanking ? attributes.rankingField : 'Do not rank'}
                disabled={mode === 'view' || !attributes.useRanking}
                error={
                  attributes.useRanking &&
                  (!attributes.rankingField ||
                    !attributes.query
                      ?.toLowerCase()
                      .includes(attributes.rankingField.toLowerCase()))
                }
                onChange={event => onChange({ ...attributes, rankingField: event.target.value })}
              />
              {attributes.useRanking &&
                (!attributes.rankingField ||
                  !attributes.query
                    ?.toLowerCase()
                    .includes(attributes.rankingField.toLowerCase())) && (
                  <div style={{ color: 'red' }}>
                    <Text size="small">Field not found in query</Text>
                  </div>
                )}
            </PropertyRow>
            <PropertyRow>
              <PropertyTitle>Deduplication fields</PropertyTitle>
              <SwitchBase
                checked={attributes.useDeduplication}
                disabled={mode !== 'edit'}
                description="Enable deduplication"
                onChange={event =>
                  onChange({ ...attributes, useDeduplication: event.target.checked })
                }
              />
            </PropertyRow>
            <Container>
              <Text size="small">
                When enabled, the query will be deduplicated by the specified fields together with
                entity_id and entity_type. This means that new tasks will be created only if the
                query returns a new combination of the specified fields, entity_id and entity_type.
              </Text>
              {attributes.useDeduplication && (
                <>
                  <DeduplicationGrid>
                    <Text size="small">
                      <li>Entity ID</li>
                    </Text>
                    <div />
                    <Text size="small">
                      <li>Entity Type</li>
                    </Text>
                    <div />
                    {attributes.deduplicationFields?.map((f: string) => (
                      <>
                        <Text size="small" key={f}>
                          <li>{f}</li>
                        </Text>
                        <IconButton
                          icon={TrashIcon}
                          accessibleLabel="Delete"
                          disabled={mode !== 'edit'}
                          onPress={() =>
                            onChange({
                              ...attributes,
                              deduplicationFields: attributes.deduplicationFields.filter(
                                (field: string) => field !== f
                              ),
                            })
                          }
                        />
                      </>
                    ))}
                    {mode === 'edit' && (
                      <>
                        <Input
                          type="text"
                          value={newDeduplicationField}
                          disabled={mode !== 'edit'}
                          error={
                            !attributes.query
                              ?.toLowerCase()
                              .includes(newDeduplicationField.toLowerCase())
                          }
                          onChange={event => setNewDeduplicationField(event.target.value)}
                        />
                        <IconButton
                          icon={ConfirmIcon}
                          accessibleLabel="Add"
                          onPress={() => {
                            onChange({
                              ...attributes,
                              deduplicationFields: [
                                ...(attributes.deduplicationFields || []),
                                newDeduplicationField,
                              ],
                            })
                            setNewDeduplicationField('')
                          }}
                          disabled={
                            newDeduplicationField === '' ||
                            attributes.deduplicationFields?.includes(newDeduplicationField) ||
                            !attributes.query
                              ?.toLowerCase()
                              .includes(newDeduplicationField.toLowerCase())
                          }
                        />
                      </>
                    )}
                  </DeduplicationGrid>
                  {!attributes.query?.toLowerCase().includes(newDeduplicationField.toLowerCase()) &&
                    mode === 'edit' && (
                      <div style={{ color: 'red' }}>
                        <Text size="small">Field not found in query</Text>
                      </div>
                    )}
                </>
              )}
              {!attributes.useDeduplication && (
                <Text weight="bold" size="small">
                  Deduplication is disabled
                </Text>
              )}
              {attributes.useDeduplication && mode === 'override' && (
                <>
                  <PropertyTitle>
                    Deduplication Group
                    <TooltipContainerNotFullWidth>
                      <Tooltip placement="bottom" target={<HelpIcon />}>
                        You may want to deduplicate across multiple pipelines. If so, enter the same
                        value here for all pipelines. If this is left blank we use
                        &quot;pipeline_&lt;step_id&gt;&quot;, where step_id can be found on the list
                        page or in the URL. Changing this value will cause all existing
                        deduplication to be invalidated.
                      </Tooltip>
                    </TooltipContainerNotFullWidth>
                  </PropertyTitle>
                  <Input
                    type="text"
                    style={{ width: '100%' }}
                    value={attributes.deduplicationGroup}
                    disabled={mode !== 'override'}
                    onChange={event =>
                      onChange({ ...attributes, deduplicationGroup: event.target.value })
                    }
                  />
                </>
              )}
            </Container>
          </Container>
          <Divider />
          <Container style={{ marginTop: spacing.X16 }}>
            <Text weight="bold">Step Output</Text>
            <Text size="small">
              This step will perform the query against a Medium warehouse in snowflake. Returned
              rows will be turned into tasks with columns as context in the task, which can be used
              by other CAMP steps or viewed in results files. For example, if your query was:
            </Text>
            <TextInput
              disabled={true}
              css={{ height: '180px' }}
              value={i18n.queryResponseExample}
            />
            <Text size="small">The query might return rows like the following:</Text>
            <ExampleGrid>
              <Text weight="semibold" size="small">
                {i18n.queryResponseExampleEntityId}
              </Text>
              <Text weight="semibold" size="small">
                {i18n.queryResponseExampleEntityType}
              </Text>
              <Text weight="semibold" size="small">
                {i18n.queryResponseExampleDisplayName}
              </Text>
              <Text weight="semibold" size="small">
                {i18n.queryResponseExampleImageUrl}
              </Text>
              <ExampleGridItem size="small">{i18n.entityIdExample1}</ExampleGridItem>
              <ExampleGridItem size="small">{i18n.entityTypeExample}</ExampleGridItem>
              <ExampleGridItem size="small">{i18n.displayNameExample1}</ExampleGridItem>
              <ExampleGridItem size="small">{i18n.imageUrlExample1}</ExampleGridItem>
              <ExampleGridItem size="small">{i18n.entityIdExample2}</ExampleGridItem>
              <ExampleGridItem size="small">{i18n.entityTypeExample}</ExampleGridItem>
              <ExampleGridItem size="small">{i18n.displayNameExample2}</ExampleGridItem>
              <div />
              <ExampleGridItem size="small">{i18n.entityIdExample3}</ExampleGridItem>
              <ExampleGridItem size="small">{i18n.entityTypeExample}</ExampleGridItem>
              <ExampleGridItem size="small">{i18n.displayNameExample3}</ExampleGridItem>
              <ExampleGridItem size="small">{i18n.imageUrlExample3}</ExampleGridItem>
              <ExampleGridItem size="small">{i18n.entityIdExample4}</ExampleGridItem>
              <ExampleGridItem size="small">{i18n.entityTypeExample}</ExampleGridItem>
              <ExampleGridItem size="small">{i18n.displayNameExample4}</ExampleGridItem>
              <ExampleGridItem size="small">{i18n.imageUrlExample4}</ExampleGridItem>
              <ExampleGridItem size="small">{i18n.entityIdExample5}</ExampleGridItem>
              <ExampleGridItem size="small">{i18n.entityTypeExample}</ExampleGridItem>
              <ExampleGridItem size="small">{i18n.displayNameExample5}</ExampleGridItem>
              <div />
            </ExampleGrid>
            <Text size="small">
              The returned rows will be turned into tasks with these context fields, which can be
              used by other CAMP steps or viewed in results files:
            </Text>
            <TextInput disabled={true} css={{ height: '710px' }} value={i18n.outputExample} />
          </Container>
        </PropertySectionLeft>
        <PropertySectionRight>
          <Property style={{ height: '100%' }}>
            <PropertyTitle>Query</PropertyTitle>
            <Text size="small">{i18n.queryTooltip}</Text>
            <TextInput
              css={{ fontFamily: 'Courier', fontSize: fontSize.X15, resize: 'vertical' }}
              disabled={mode !== 'edit'}
              onChange={e => {
                onChange({
                  ...attributes,
                  query: e.target.value,
                })
              }}
              value={attributes.query}
            />
          </Property>
        </PropertySectionRight>
      </PropertyContainer>
    </>
  )
}

export default SnowflakeQueryInputForm
